import classNames from 'classnames';
import { forwardRef } from 'react';
import styles from './LightRetouchDropdown.module.scss';

type LightRetouchDropdownProps = React.HTMLAttributes<HTMLUListElement> & {
    children: React.ReactNode;
    className?: string;
};

/**
 * Light Retouch dropdown panel (listbox) — Figma `Dropdown` / `_dropdown-button-structure`.
 * Pair with `LightRetouchDropdownItem` for options. Size and width are consumer-controlled via `className`.
 */
export const LightRetouchDropdown = forwardRef<HTMLUListElement, LightRetouchDropdownProps>(
    ({ children, className, ...props }, ref) => (
        <ul
            ref={ref}
            role="listbox"
            className={classNames(styles.panel, className)}
            data-testid="light-retouch-dropdown"
            {...props}
        >
            {children}
        </ul>
    ),
);

LightRetouchDropdown.displayName = 'LightRetouchDropdown';

export type LightRetouchDropdownItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    selected?: boolean;
    highlighted?: boolean;
    className?: string;
    itemClassName?: string;
    buttonRef?: React.Ref<HTMLButtonElement>;
};

/**
 * Single option inside `LightRetouchDropdown`.
 * Selected and keyboard-highlighted states share the Figma background treatment, while only
 * `selected` controls `aria-selected`.
 */
export const LightRetouchDropdownItem = forwardRef<HTMLLIElement, LightRetouchDropdownItemProps>(
    (
        {
            children,
            className,
            itemClassName,
            buttonRef,
            selected = false,
            highlighted = false,
            type = 'button',
            ...buttonProps
        },
        ref,
    ) => (
        <li ref={ref} role="presentation" className={classNames(styles.itemWrapper, className)}>
            <button
                ref={buttonRef}
                type={type}
                role="option"
                aria-selected={selected}
                className={classNames(
                    styles.item,
                    {
                        [styles.itemSelected]: selected,
                        [styles.itemHighlighted]: highlighted,
                    },
                    itemClassName,
                )}
                data-testid="light-retouch-dropdown-item"
                {...buttonProps}
            >
                {children}
            </button>
        </li>
    ),
);

LightRetouchDropdownItem.displayName = 'LightRetouchDropdownItem';

export default LightRetouchDropdown;
